import {
  Box,
  Button,
  Divider,
  FormControl,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { FormEvent, useCallback, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import HolaVidaLogo from "src/assets/HolaVidaLogo";
import VisibilityOutlined from "@mui/icons-material/VisibilityOutlined";
import VisibilityOffOutline from "@mui/icons-material/VisibilityOffOutlined";
import { useDispatch } from "react-redux";
import { patientApi } from "src/api/services/patientApi";
import { useIsMobile } from "src/hooks/useIsMobile";
import { AppDispatch } from "src/app/store";
import { supabaseClient } from "src/lib/supabase/supabaseClient";
import LoadingButton from "src/components/Buttons/LoadingButton";
import { Provider } from "@supabase/supabase-js";
import AppleLogo from "src/assets/sso-signup-logos/AppleLogo";
import FacebookLogo from "src/assets/sso-signup-logos/FacebookLogo";
import GoogleLogo from "src/assets/sso-signup-logos/GoogleLogo";
import TermsAndConditions from "./TermsAndConditions";
import { Trans, useTranslation } from "react-i18next";
import { topLevelDomains } from "src/constants/topLevelDomains";

interface SignUpFormProps {
  isSignUp: boolean;
  onSuccess: (e: string) => Promise<void>;
  productSlug: string;
  referralCode?: string | null;
  variant?: string | null;
}

const SignUpForm = ({
  isSignUp,
  onSuccess,
  productSlug,
  referralCode,
  variant,
}: SignUpFormProps) => {
  const [email, setEmail] = useState<string>("");
  const [displayPasswordInput, setDisplayPasswordInput] =
    useState<boolean>(false);
  const [password, setPassword] = useState<string>("");
  const [passwordVisibility, setPasswordVisibility] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>();
  const isMobile = useIsMobile();
  const dispatch = useDispatch<AppDispatch>();

  const { t, i18n } = useTranslation();
  const page = isSignUp ? "signup" : "login";

  const enableButton = useMemo(
    () =>
      email.includes("@") &&
      topLevelDomains.some((tld) => email.toLowerCase().endsWith(tld)),
    [email],
  );

  const onSubmit = useCallback(
    async (e: FormEvent<HTMLFormElement>) => {
      e.preventDefault();

      setLoading(true);
      if (!email || !password) {
        setError("Please enter both email and password");
        setLoading(false);
        return;
      }

      if (productSlug == null && isSignUp === true) {
        setError("Oops, something went wrong");
        setLoading(false);
        return;
      }

      try {
        const { data, error } = isSignUp
          ? await supabaseClient.auth.signUp({
              email,
              password,
            })
          : await supabaseClient.auth.signInWithPassword({ email, password });

        if (error) {
          if (error.code === "user_banned") {
            setError(
              t(
                "signup_form.user_banned",
                // "Your account is not eligible for access. Please reach out to our support team support@holavida.com.",
              ),
            );
            setLoading(false);
            return false;
          }

          if (error.code === "weak_password") {
            setError(
              t(
                "signup_form.weak_password",
                "Your password must be at least 6 characters long.",
              ),
            );
            setLoading(false);
            return false;
          }

          setError(error.message);
          setLoading(false);
          return false;
        }

        if (data.user != null) {
          await onSuccess(data.user?.id);
          window.freshpaint?.addEventProperties({ email: email });
          dispatch(patientApi.endpoints.getMe.initiate());
          setEmail("");
          setPassword("");
        }
      } catch (e) {
        console.error("Unexpected error", e);
      } finally {
        setLoading(false);
      }
    },
    [dispatch, email, isSignUp, onSuccess, password, productSlug, t],
  );

  const handleContinue = useCallback(
    async (e: FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      if (email != "" && password != "") {
        await onSubmit(e);
      } else {
        setDisplayPasswordInput(true);
      }
    },
    [email, onSubmit, password],
  );

  const handleSignUpWithOAuth = useCallback(
    async (provider: Provider) => {
      if (productSlug == null && isSignUp === true) {
        setError("Oops, something went wrong");
        setLoading(false);
        return;
      }

      localStorage.setItem("isSignUp", isSignUp.toString());

      const params = new URLSearchParams({
        isSignUp: isSignUp.toString(),
        product: productSlug,
        ...(variant != null && { variant }),
        ...(referralCode != null && { referral_code: referralCode }),
      });

      const { error } = await supabaseClient.auth.signInWithOAuth({
        provider: provider,
        options: {
          redirectTo: `${import.meta.env.VITE_BASE_URL}/transition${
            params.toString() ? `?${params}` : ""
          }`,
        },
      });

      if (error) {
        setError(error.message || "Something went wrong, please try again.");
        setLoading(false);
        return false;
      }
    },
    [productSlug, isSignUp, variant, referralCode],
  );

  const title = useMemo(() => {
    if (
      [
        "fhl",
        "bc",
        "mhl-ot",
        "fhl-ot",
        "ed-ot",
        "amh-ot",
        "aa-ot",
        "ac-ot",
        "ro-ot",
        "bb-ot",
        "sl-ot",
        "iwl-ot",
      ].includes(productSlug) &&
      isSignUp
    ) {
      return t(`${page}.${productSlug}.title`);
    }

    return t(`${page}.title`);
  }, [isSignUp, page, productSlug, t]);

  const subtitle = useMemo(() => {
    if (
      [
        "fhl",
        "bc",
        "mhl-ot",
        "fhl-ot",
        "ed-ot",
        "amh-ot",
        "aa-ot",
        "ac-ot",
        "ro-ot",
        "bb-ot",
        "sl-ot",
      ].includes(productSlug) &&
      isSignUp
    ) {
      return t(`${page}.${productSlug}.subtitle`);
    }

    return t(`${page}.subtitle`);
  }, [isSignUp, page, productSlug, t]);

  const subtext = useMemo(
    () => (
      <Trans i18nKey={`${page}.subtext`}>
        Have an account?{" "}
        <Link
          to={isSignUp ? "/login" : `/${i18n.language}/${productSlug}/signup`}
          style={{ fontWeight: 700, textDecoration: "none" }}
        >
          Log in
        </Link>
      </Trans>
    ),
    [i18n.language, isSignUp, page, productSlug],
  );

  return (
    <>
      <Stack gap="3rem">
        <Box
          component="form"
          role="form"
          onSubmit={isSignUp ? handleContinue : onSubmit}
          sx={{
            display: "flex",
            flexDirection: "column",
            height: "max-contnet",
            maxWidth: "380px",
            width: isMobile ? "fit-content" : "100%",
            boxShadow: 10,
            p: isMobile ? 3 : 5.5,
            borderRadius: "12px",
            backgroundColor: "#ffffff",
          }}
        >
          <Box sx={{ alignSelf: "center" }}>
            <HolaVidaLogo
              style={{
                width: isMobile ? "145" : "180",
                height: isMobile ? "80" : "90",
              }}
            />
          </Box>
          <Stack gap={2} sx={{ mt: 1 }}>
            <Typography
              variant={isMobile ? "h6" : "h5"}
              fontWeight={700}
              width={isMobile ? "320px" : "400px"}
            >
              {title}
            </Typography>
            <Typography color="#6C7278">{subtitle}</Typography>
          </Stack>
          <Stack gap={4} sx={{ mt: 3 }}>
            <Stack gap={3}>
              <FormControl variant="filled" fullWidth required>
                <TextField
                  required
                  type="email"
                  id="filled-adornment-email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  label={t(`${page}.email.label`)}
                />
              </FormControl>
              {!isSignUp || displayPasswordInput ? (
                <FormControl variant="filled" fullWidth required>
                  <TextField
                    required
                    type={passwordVisibility ? "text" : "password"}
                    id="filled-adornment-password"
                    label={t(`${page}.password.label`)}
                    slotProps={{
                      input: {
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              edge="end"
                              onClick={() => setPasswordVisibility((o) => !o)}
                              onMouseDown={() =>
                                setPasswordVisibility((o) => !o)
                              }
                            >
                              {passwordVisibility ? (
                                <VisibilityOutlined />
                              ) : (
                                <VisibilityOffOutline />
                              )}
                            </IconButton>
                          </InputAdornment>
                        ),
                      },
                    }}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                  />
                </FormControl>
              ) : null}
              {!isSignUp ? (
                <Link to="/reset-password" style={{ alignSelf: "end" }}>
                  {t(`${page}.forgotPassword`)}
                </Link>
              ) : null}
            </Stack>
            {error ? (
              <Typography sx={{ color: "red" }} textAlign="center">
                {error.toString()}
              </Typography>
            ) : null}
            <Stack gap={2}>
              <LoadingButton
                type="submit"
                loading={loading}
                disabled={isSignUp && !enableButton}
              >
                {t(`${page}.submit`)}
              </LoadingButton>
              <Divider>{t(`${page}.or`)}</Divider>

              <Stack gap={1}>
                <Button
                  variant="outlined"
                  size="small"
                  sx={{
                    backgroundColor: "#fff",
                    border: "1px solid var(--Secondary-200, #DCE4E8)",
                    borderRadius: "10px",
                    color: "#000",
                    padding: "13px 24px",
                  }}
                  startIcon={<AppleLogo />}
                  onClick={() => handleSignUpWithOAuth("apple")}
                >
                  {t(`${page}.sso`, { provider: "Apple" })}
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  sx={{
                    backgroundColor: "#fff",
                    border: "1px solid var(--Secondary-200, #DCE4E8)",
                    borderRadius: "10px",
                    color: "#000",
                    padding: "13px 24px",
                  }}
                  startIcon={<FacebookLogo />}
                  onClick={() => handleSignUpWithOAuth("facebook")}
                >
                  {t(`${page}.sso`, { provider: "Facebook" })}
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  sx={{
                    backgroundColor: "#fff",
                    border: "1px solid var(--Secondary-200, #DCE4E8)",
                    borderRadius: "10px",
                    color: "#000",
                    padding: "13px 24px",
                  }}
                  startIcon={<GoogleLogo />}
                  onClick={() => handleSignUpWithOAuth("google")}
                >
                  {t(`${page}.sso`, { provider: "Google" })}
                </Button>
              </Stack>
            </Stack>
            <Stack gap={1}>
              {isSignUp ? <TermsAndConditions /> : null}
              <Typography sx={{ position: "relative" }} alignSelf="center">
                {subtext}
              </Typography>
            </Stack>
          </Stack>
        </Box>
      </Stack>
    </>
  );
};

export default SignUpForm;
